import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { usePortalContainer } from "@/common/hooks/use-portal-container";
import { DialogPortalProps } from "./types";

/**
 * Dialog Portal Component
 * Handles portal rendering with automatic shadow DOM container detection
 */
export const DialogPortal: React.FC<DialogPortalProps> = ({
  container: containerProp,
  ...props
}) => {
  // Automatically detect shadow root container if not explicitly provided
  // Memoize to prevent hook re-execution on every render
  const autoContainer = usePortalContainer();
  const container = React.useMemo(
    () => containerProp || autoContainer,
    [containerProp, autoContainer]
  );
  
  // Error check for invalid container prop (only log errors, not debug info)
  React.useEffect(() => {
    if (containerProp instanceof ShadowRoot) {
      console.error('[ShadowDOM] ❌ ERROR: containerProp is ShadowRoot! This should be HTMLElement');
      console.error('[ShadowDOM] Stack trace:', new Error().stack);
    }
  }, [containerProp]);
  
  // Verify container is connected (only log errors)
  React.useEffect(() => {
    if (container instanceof HTMLElement && !container.isConnected) {
      console.error('[ShadowDOM] ❌ Container is NOT connected to DOM!');
    } else if (container instanceof ShadowRoot) {
      console.warn('[ShadowDOM] ⚠️ Container is ShadowRoot - Radix Portal may ignore it and render to shadow root directly');
    }
  }, [container]);
  
  // Fix scrolling for scrollable elements in Shadow DOM
  // Prevent WhatsApp from intercepting wheel events by stopping propagation
  React.useEffect(() => {
    if (!(container instanceof HTMLElement)) return;

    const rootNode = container.getRootNode();
    const isInShadowDOM = rootNode instanceof ShadowRoot;
    if (!isInShadowDOM) return;

    const handleWheel = (e: WheelEvent) => {
      // Stop propagation to prevent WhatsApp from intercepting
      // Native scrolling will work normally within the portal
      e.stopPropagation();
    };

    container.addEventListener('wheel', handleWheel, { 
      passive: false,
      capture: true // Capture before WhatsApp's handlers
    });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [container]);
  
  return <DialogPrimitive.Portal container={container} {...props} />;
};

DialogPortal.displayName = DialogPrimitive.Portal.displayName;

